
"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { AutoComplete, Input } from "antd";
import type { InputRef } from "antd";
import { SearchOutlined, SettingOutlined, CloseOutlined, FireOutlined, SwapRightOutlined } from "@ant-design/icons";
import { FaCompass, FaCaravan, FaCampground, FaLeaf } from "react-icons/fa";
import CustomRadiusSlider from "./CustomRadiusSlider";
import { AssistantMessage } from "../types/assistant";

const POPULAR_SEARCHES = [
  "Byron Bay",
  "Margaret River",
  "Noosa",
  "Great Ocean Road",
  "Kimberley",
  "Bright",
  "Jervis Bay",
  "Blue Mountains",
];

const POPULAR_ROUTES = [
  "Sydney to Byron Bay",
  "Melbourne to Adelaide",
  "Cairns to Cape Tribulation",
  "Perth to Esperance",
  "Adelaide to Alice Springs",
  "Hobart to Cradle Mountain",
];

const TAB_ICONS: Record<string, React.ReactNode> = {
  All: <FaCompass className="text-[12px]" />,
  "Caravan Parks": <FaCaravan className="text-[12px]" />,
  "Camping Sites": <FaCampground className="text-[12px]" />,
  "Free Camping": <FaLeaf className="text-[12px]" />,
};

interface HeroSearchProps {
  searchQuery: string;
  selectedType: string;
  showFilters: boolean;
  selectedFeatures: string[];
  radius: number;
  options: { value: string }[];
  isSearching: boolean;
  onLocationSearch: (value: string) => void;
  onLocationSelect: (value: string) => void;
  onLocationChange: (value: string) => void;
  onTypeChange: (type: string) => void;
  onToggleFilters: () => void;
  onToggleFeature: (feature: string) => void;
  onClearFilters: () => void;
  onRadiusChange: (value: number) => void;
  onSearch: () => void;
  onPressEnter: () => void;
  features: string[];
  assistantMessages: AssistantMessage[];
  assistantSuggestions: string[];
  assistantIsOpen: boolean;
  assistantHasUnread: boolean;
  assistantIsProcessing: boolean;
  onToggleAssistant: () => void;
  onSendAssistant: (message: string) => void;
}

const baseTypeOptions = [
  { value: "All", label: "All" },
  { value: "Caravan Parks", label: "Caravan Parks" },
  { value: "Camping Sites", label: "Camping Sites" },
  { value: "Free Camping", label: "Free Camping" },
];

const HeroSearch: React.FC<HeroSearchProps> = ({
  searchQuery,
  selectedType,
  showFilters,
  selectedFeatures,
  radius,
  options,
  isSearching,
  onLocationSearch,
  onLocationSelect,
  onLocationChange,
  onTypeChange,
  onToggleFilters,
  onToggleFeature,
  onClearFilters,
  onRadiusChange,
  onSearch,
  onPressEnter,
  features,
  assistantMessages,
  assistantSuggestions,
  assistantIsOpen,
  assistantHasUnread,
  assistantIsProcessing,
  onToggleAssistant,
  onSendAssistant,
}) => {
  const [showAllFeatureGroups, setShowAllFeatureGroups] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const searchInputRef = useRef<InputRef>(null);

  // Keyboard shortcut: Cmd/Ctrl+K to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Track mobile viewport to adapt layout/controls for small screens.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 640px)");
    const sync = (event?: MediaQueryListEvent) => {
      setIsMobile(event ? event.matches : mql.matches);
    };
    sync();
    mql.addEventListener("change", sync);
    return () => mql.removeEventListener("change", sync);
  }, []);

  const typeOptions = useMemo(() => {
    if (
      !selectedType ||
      baseTypeOptions.find((option) => option.value === selectedType)
    ) {
      return baseTypeOptions;
    }
    return [...baseTypeOptions, { value: selectedType, label: selectedType }];
  }, [selectedType]);

  const featureGroups = useMemo(() => {
    const groups = [
      {
        title: "Essentials & Amenities",
        items: [
          "Powered Sites",
          "Unpowered Sites",
          "Toilets",
          "Hot Showers",
          "Showers",
          "Drinking Water",
          "Camp Kitchen",
          "Laundry",
          "Dump Point",
          "Accessible Facilities",
          "Campfires Allowed",
        ],
      },
      {
        title: "Comfort & Services",
        items: [
          "Cabins",
          "Glamping",
          "WiFi",
          "BBQ",
          "Playground",
          "Swimming Pool",
          "Family Friendly",
          "Pet Friendly",
          "On-site Cafe",
          "Firewood Available",
        ],
      },
      {
        title: "Outdoor Adventures",
        items: [
          "River Access",
          "Beach Access",
          "Lake Access",
          "Boat Ramp",
          "Fishing",
          "Hiking Trails",
          "Cycling Trails",
          "4WD Access",
          "Horse Riding",
          "Stargazing",
        ],
      },
      {
        title: "Points of Interest",
        items: [
          "Lookout",
          "Waterfall",
          "Scenic Drive",
          "National Park Visitor Centre",
          "Wildlife Park",
          "Botanic Gardens",
          "Historic Site",
          "Heritage Walk",
          "Museum",
          "Art Gallery",
          "Winery",
          "Brewery",
          "Distillery",
          "Farm Gate",
          "Local Market",
        ],
      },
    ];

    const assigned = new Set<string>();
    const resolvedGroups = groups
      .map((group) => ({
        title: group.title,
        items: group.items.filter((item) => {
          if (!features.includes(item)) return false;
          assigned.add(item);
          return true;
        }),
      }))
      .filter((group) => group.items.length > 0);

    const remaining = features.filter((feature) => !assigned.has(feature));
    if (remaining.length > 0) {
      resolvedGroups.push({ title: "More", items: remaining });
    }

    return resolvedGroups;
  }, [features]);

  const groupsToRender = showAllFeatureGroups
    ? featureGroups
    : featureGroups.slice(0, 3);

  useEffect(() => {
    if (!showFilters) {
      setShowAllFeatureGroups(false);
    }
  }, [showFilters]);

  return (
    <section className="relative isolate overflow-hidden">
      <div className="absolute inset-0">
        <Image
          src="/camping-hero.webp"
          alt="Find Your Perfect Campsite"
          fill
          priority
          quality={90}
          sizes="100vw"
          className="h-full w-full object-cover object-[30%_center]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/70 via-slate-900/40 to-indigo-950/20" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-slate-900/50 to-transparent" />
      </div>

      <div
        className={`relative mx-auto flex max-w-6xl flex-col ${isMobile ? "gap-4 px-3 py-6" : "gap-5 px-4 py-11"} text-white md:gap-6 md:px-6 lg:py-14`}
      >
        <div className={`max-w-3xl space-y-3 animate-hero-text ${isMobile ? "text-center" : ""}`}>
          <span className="inline-flex items-center rounded-full border border-white/20 bg-gradient-to-r from-sky-500/20 to-indigo-500/20 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white/85 shadow-sm backdrop-blur">
            <span className="hidden sm:inline">Australia&apos;s Camping Search Engine</span>
            <span className="sm:hidden">Camping Search Engine</span>
          </span>
          <h1 className="text-3xl font-semibold leading-tight drop-shadow md:text-5xl lg:text-6xl">
            Discover Australia&apos;s best camping spots
          </h1>
          <p className="text-sm text-white/70 md:text-base">
            Search thousands of campsites, caravan parks and free camps.
            Plan your perfect route — all in one place.
          </p>
          <div className={`flex items-center gap-2 text-xs font-medium text-white/50 flex-wrap ${isMobile ? "justify-center" : ""}`}>
            <span className="whitespace-nowrap">8,000+ campsites</span>
            <span className="text-white/30">·</span>
            <span className="whitespace-nowrap">1,200+ free camps</span>
            <span className="text-white/30">·</span>
            <span className="whitespace-nowrap">All of Australia</span>
          </div>
        </div>

        <div className={`animate-hero-card w-full rounded-3xl border border-white/20 ring-1 ring-white/10 bg-white/90 shadow-[0_40px_70px_-25px_rgba(15,23,42,0.35)] backdrop-blur-xl ${isMobile ? "pb-3 mb-2" : "pb-5"}`}>
          <div
            className={`flex ${isMobile ? "flex-nowrap overflow-x-auto" : "flex-wrap"} items-center gap-2 border-b border-slate-200/70 ${isMobile ? "px-3 py-2" : "px-4 py-3 md:px-6"}`}
          >
            {typeOptions.map((option) => {
              const isActive = selectedType === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onTypeChange(option.value)}
                  className={`relative flex flex-shrink-0 items-center gap-1.5 rounded-full ${isMobile ? "px-3 py-2 text-[11px]" : "px-4 py-2 text-xs md:text-sm"} font-semibold transition-all ${
                    isActive
                      ? "bg-slate-900 text-white shadow-md"
                      : "bg-white/50 text-slate-500 hover:bg-white/80 hover:text-slate-800 hover:scale-[1.02]"
                  }`}
                >
                  {TAB_ICONS[option.value] && (
                    <span className={isActive ? "opacity-100" : "opacity-60"}>
                      {TAB_ICONS[option.value]}
                    </span>
                  )}
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className={`${isMobile ? "px-3 py-4" : "px-4 py-4 md:px-6 md:py-6"} flex flex-col ${isMobile ? "gap-4" : "gap-4"}`}>
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="flex-1">
                <AutoComplete
                  options={options}
                  value={searchQuery}
                  onSelect={onLocationSelect}
                  onSearch={onLocationSearch}
                  className="w-full"
                  style={{ height: 'auto' }}
                >
                  <Input
                    ref={searchInputRef}
                    size="large"
                    prefix={<SearchOutlined style={{ color: "#a1a1aa" }} />}
                    suffix={
                      <kbd className="hidden md:inline-flex items-center gap-0.5 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
                        <span className="text-[9px]">{typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) ? '\u2318' : 'Ctrl'}</span>K
                      </kbd>
                    }
                    placeholder="Search by suburb, landmark or state"
                    onPressEnter={onPressEnter}
                    onChange={(event) => onLocationChange(event.target.value)}
                    className={`${isMobile ? "h-11" : "h-12"} rounded-[14px] border border-slate-200/70 text-[15px] text-slate-700 shadow-inner`}
                  />
                </AutoComplete>
              </div>

              <div className={`${isMobile ? "grid grid-cols-2 gap-2" : "md:flex md:w-auto md:items-center md:gap-3"}`}>
                <button
                  type="button"
                  onClick={onToggleFilters}
                  className={`group inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                    showFilters || selectedFeatures.length > 0
                      ? "bg-slate-900 text-white shadow-[0_12px_30px_rgba(15,23,42,0.25)]"
                      : "border border-slate-200/70 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-800"
                  }`}
                >
                  <SettingOutlined className="text-base" />
                  <span className="hidden sm:inline">Filters</span>
                  {selectedFeatures.length > 0 && (
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-slate-900">
                      {selectedFeatures.length}
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={onSearch}
                  disabled={isSearching}
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-[0_15px_35px_rgba(14,116,144,0.35)] transition ${
                    isSearching
                      ? "cursor-not-allowed bg-slate-400/80"
                      : "bg-gradient-to-r from-sky-500 to-indigo-500 hover:brightness-110 hover:shadow-[0_0_20px_rgba(56,189,248,0.3)]"
                  }`}
                >
                  {isSearching ? (
                    <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
                  ) : (
                    <SearchOutlined className="text-base" />
                  )}
                  <span>Search</span>
                </button>
              </div>
            </div>

            {/* Popular searches & routes — shown when input is empty */}
            {!searchQuery && (
              <div className={`flex flex-col ${isMobile ? "gap-1" : "gap-2"}`}>
                <div className={`flex items-center gap-2 ${isMobile ? "overflow-x-auto pb-1 -mx-1 px-1" : "flex-wrap"}`}>
                  <span className="flex-shrink-0 flex items-center gap-1 text-[11px] font-medium text-slate-400">
                    <FireOutlined className="text-orange-400 text-[10px]" />
                    Popular
                  </span>
                  {POPULAR_SEARCHES.map((place) => (
                    <button
                      key={place}
                      type="button"
                      onClick={() => {
                        onLocationChange(place);
                        onLocationSelect(place);
                      }}
                      className="flex-shrink-0 rounded-full border border-slate-200/60 bg-white/60 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-slate-300 hover:bg-white hover:text-slate-800"
                    >
                      {place}
                    </button>
                  ))}
                </div>
                <div className={`flex items-center gap-2 ${isMobile ? "overflow-x-auto pb-1 -mx-1 px-1" : "flex-wrap"}`}>
                  <span className="flex-shrink-0 flex items-center gap-1 text-[11px] font-medium text-slate-400">
                    <SwapRightOutlined className="text-sky-400 text-[10px]" />
                    Routes
                  </span>
                  {POPULAR_ROUTES.map((route) => (
                    <button
                      key={route}
                      type="button"
                      onClick={() => {
                        onLocationChange(route);
                        onLocationSelect(route);
                      }}
                      className="flex-shrink-0 rounded-full border border-sky-200/60 bg-sky-50/60 px-3 py-1 text-xs font-medium text-sky-700 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
                    >
                      {route}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Active filter tags — shown when filters are selected but panel is closed */}
            {selectedFeatures.length > 0 && !showFilters && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-medium text-slate-400">Filters:</span>
                {selectedFeatures.slice(0, 3).map((feature) => (
                  <span
                    key={feature}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700"
                  >
                    {feature}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFeature(feature);
                      }}
                      className="ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                      aria-label={`Remove ${feature} filter`}
                    >
                      <CloseOutlined className="text-[8px]" />
                    </button>
                  </span>
                ))}
                {selectedFeatures.length > 3 && (
                  <button
                    type="button"
                    onClick={onToggleFilters}
                    className="text-[11px] font-medium text-slate-400 hover:text-slate-600"
                  >
                    +{selectedFeatures.length - 3} more
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClearFilters}
                  className="text-[11px] font-medium text-slate-400 hover:text-red-500"
                >
                  Clear all
                </button>
              </div>
            )}

            {showFilters && (
              <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-4 shadow-inner backdrop-blur">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
                      Refine results
                    </p>
                    <h3 className="text-lg font-semibold text-slate-900">
                      Filter by features
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={onClearFilters}
                    className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400 hover:text-slate-600"
                  >
                    Clear all
                  </button>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {groupsToRender.map((group) => (
                    <div key={group.title} className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-slate-400">
                        {group.title}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {group.items.map((feature) => {
                          const isActive = selectedFeatures.includes(feature);
                          return (
                            <button
                              key={feature}
                              type="button"
                              onClick={() => onToggleFeature(feature)}
                              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                                isActive
                                  ? "bg-slate-900 text-white shadow-[0_10px_30px_rgba(15,23,42,0.3)]"
                                  : "border border-slate-200/80 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
                              }`}
                            >
                              {feature}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {featureGroups.length > 2 && (
                  <button
                    type="button"
                    onClick={() => setShowAllFeatureGroups((prev) => !prev)}
                    className="mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-slate-400 hover:text-slate-600"
                  >
                    {showAllFeatureGroups ? "Show fewer features" : "Show all features"}
                  </button>
                )}

                <div className="mt-6 rounded-2xl border border-slate-200/60 bg-white/70 p-4 shadow-inner">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-slate-400">
                    Search radius (km)
                  </p>
                  <div className="mt-2">
                    <CustomRadiusSlider
                      value={radius}
                      onChange={onRadiusChange}
                      min={5}
                      max={200}
                      step={5}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSearch;
